import { DailyHistory, LevelProgressState, StatsStore } from '../types';
import { REGIONAL_MODES, MODE_LABELS } from '../utils/modes';
import { ExperienceProgress } from './ExperienceProgress';

interface Props {
  stats: StatsStore;
  history: DailyHistory;
  onBack: () => void;
  totalExp: number;
  levelProgress: LevelProgressState;
}

export function StatsPanel({ stats, history, onBack, totalExp, levelProgress }: Props) {
  const entries = Object.entries(history).sort((a, b) => b[0].localeCompare(a[0]));
  const totalCorrect = Object.values(stats).reduce((sum, record) => sum + record.totalCorrectGuesses, 0);
  return (
    <section>
      <div className="page-header">
        <div className="title-stack">
          <p className="eyebrow">Lifetime performance</p>
          <h1>Stats</h1>
          <p className="helper-text">Daily history is stored locally per device.</p>
        </div>
        <button className="btn ghost" onClick={onBack}>
          Back
        </button>
      </div>
      <div className="menu-grid">
        <article className="menu-card full-span level-card">
          <ExperienceProgress totalExp={totalExp} animateFrom={totalExp} />
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Daily</p>
              <h2>Streak focus</h2>
              <p className="helper-text">Best streak: {stats.daily.bestStreak}</p>
            </div>
            <span className="pill">{stats.daily.totalGames}</span>
          </div>
          <p className="helper-text">Completed daily challenges tracked on this device.</p>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Random</p>
              <h2>Endless runs</h2>
              <p className="helper-text">Best streak: {stats.random.bestStreak}</p>
            </div>
            <span className="pill alt">{stats.random.totalGames}</span>
          </div>
          <p className="helper-text">Keep climbing with seeded or random playthroughs.</p>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Top 100</p>
              <h2>Global giants</h2>
              <p className="helper-text">Best streak: {stats['top-100'].bestStreak}</p>
            </div>
            <span className="pill">{stats['top-100'].totalGames}</span>
          </div>
          <p className="helper-text">Only the 100 most populated cities worldwide.</p>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Local</p>
              <h2>Nearby challenges</h2>
              <p className="helper-text">Best streak: {stats.local.bestStreak}</p>
            </div>
            <span className="pill neutral">{stats.local.totalGames}</span>
          </div>
          <p className="helper-text">Games using your saved location to find nearby cities.</p>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Pin</p>
              <h2>Pinned runs</h2>
              <p className="helper-text">Best streak: {stats.pin.bestStreak}</p>
            </div>
            <span className="pill alt">{stats.pin.totalGames}</span>
          </div>
          <p className="helper-text">Runs using a pinned map location to build the city pool.</p>
        </article>
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Accuracy</p>
              <h2>Correct picks</h2>
              <p className="helper-text">Across all modes</p>
            </div>
            <span className="pill neutral">{totalCorrect}</span>
          </div>
          <p className="helper-text">How often you guessed the larger population correctly.</p>
        </article>
      </div>
      <div className="notice" style={{ marginTop: '1rem' }}>
        <h3 className="eyebrow">Region runs</h3>
        <div className="menu-grid continent-grid">
          {REGIONAL_MODES.map((mode) => (
            <article key={mode} className="menu-card">
              <div className="menu-card-header">
                <div>
                  <p className="eyebrow">{MODE_LABELS[mode]}</p>
                  <h2>Best streak: {stats[mode].bestStreak}</h2>
                  <p className="helper-text">Games played: {stats[mode].totalGames}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className="notice" style={{ marginTop: '1rem' }}>
        <h3 className="eyebrow">Daily history</h3>
        {entries.length ? (
          <ul className="history-list">
            {entries.map(([date, record]) => (
              <li key={date} className="history-item">
                <div>
                  <strong>{date}</strong>
                  <p className="helper-text">Seed: {record.seed}</p>
                </div>
                <div className="pill-group">
                  <span className="pill neutral">🔥 {record.streak ?? 0}</span>
                  <span className={`pill ${record.completed ? 'success' : 'muted'}`}>
                    {record.completed ? 'Finished' : 'In progress'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="helper-text">No daily games recorded yet.</p>
        )}
      </div>
    </section>
  );
}
